import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  Grid,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Paper,
  Divider,
  IconButton,
  Avatar,
  AvatarGroup,
  CircularProgress,
  ButtonGroup,
  Tooltip,
  ClickAwayListener,
  Stack,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Announcement as AnnouncementIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  SportsEsports as GameIcon,
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccountTree as BracketIcon,
  CalendarToday as CalendarIcon,
  Leaderboard as LeaderboardIcon,
  ListAlt as MatchesIcon
} from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import tournamentService from '../services/tournamentService';

// Helper component for circular navigation buttons
const NavButton = ({ icon, label, onClick, disabled = false }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      gap: 0.5,
      textAlign: 'center'
    }}
  >
    <IconButton
      onClick={onClick}
      disabled={disabled}
      sx={{ 
        width: 56, 
        height: 56, 
        bgcolor: disabled ? 'action.disabledBackground' : '#c89e38', 
        color: disabled ? 'action.disabled' : 'white',
        '&:hover': {
          bgcolor: disabled ? 'action.disabledBackground' : '#b38d2d'
        },
        transition: 'background-color 0.3s ease'
      }}
    >
      {icon}
    </IconButton>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

const TournamentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [openAnnouncementDialog, setOpenAnnouncementDialog] = useState(false);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [newUserId, setNewUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openTooltip, setOpenTooltip] = useState(null);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        const data = await tournamentService.getTournamentById(id);
        setTournament(data);
      } catch (error) {
        console.error('Error fetching tournament details:', error);
        setError('Error al cargar los detalles del torneo');
        // Fallback to mock data if API fails
        const mockTournament = {
          id: id, // Use the actual ID from params for consistency
          name: 'Torneo de Prueba',
          type: 'single_elimination',
          startDate: '2024-03-20',
          status: 'open',
          rules: 'Reglas del torneo...',
          participants: [
            { id: 1, name: 'Jugador 1', avatar: 'https://mui.com/static/images/avatar/1.jpg' },
            { id: 2, name: 'Jugador 2', avatar: 'https://mui.com/static/images/avatar/2.jpg' },
            { id: 3, name: 'Jugador 3', avatar: 'https://mui.com/static/images/avatar/3.jpg' },
            { id: 4, name: 'Jugador 4', avatar: 'https://mui.com/static/images/avatar/4.jpg' }
          ],
          announcements: [
            { id: 1, message: 'El torneo comenzará el 20 de marzo', date: '2024-03-15' }
          ],
          matches: [
            { id: 1, round: 1, player1: 'Jugador 1', player2: 'Jugador 2', winner: 'Jugador 1' },
            { id: 2, round: 1, player1: 'Jugador 3', player2: 'Jugador 4', winner: 'Jugador 3' },
            { id: 3, round: 2, player1: 'Jugador 1', player2: 'Jugador 3', winner: null }
          ]
        };
        setTournament(mockTournament);
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [id]);

  const handleBack = () => {
    navigate('/tournaments');
  };

  const handleJoinTournament = () => {
    // TODO: Implement join tournament functionality
  };

  const handleAddParticipant = () => {
    if (!newUserId.trim()) return;
    
    const newParticipant = {
      id: tournament.participants.length + 1,
      name: newUserId,
      avatar: `https://mui.com/static/images/avatar/${tournament.participants.length + 1}.jpg`
    };
    
    setTournament({
      ...tournament,
      participants: [...tournament.participants, newParticipant]
    });
    
    setNewUserId('');
    setOpenAddUserDialog(false);
  };

  const handleRemoveParticipant = (participantId) => {
    setTournament({
      ...tournament,
      participants: tournament.participants.filter(p => p.id !== participantId)
    });
  };

  const handlePublishAnnouncement = () => {
    if (!announcement.trim()) return;
    
    const newAnnouncement = {
      id: tournament.announcements.length + 1,
      message: announcement,
      date: new Date().toISOString()
    };
    
    setTournament({
      ...tournament,
      announcements: [...tournament.announcements, newAnnouncement]
    });
    
    setAnnouncement('');
    setOpenAnnouncementDialog(false);
  };

  // Navigation Handlers
  const handleViewParticipants = () => {
    navigate(`/tournaments/${id}/participants`);
  };

  const handleViewBracket = () => {
    localStorage.setItem('tournament_matches', JSON.stringify(tournament.matches));
    localStorage.setItem('tournament_type', tournament.type);
    navigate(`/tournaments/${id}/bracket`);
  };

  const handleViewCalendar = () => {
    navigate(`/tournaments/${id}/calendar`);
  };

  const handleViewLeaderboard = () => {
    navigate(`/tournaments/${id}/leaderboard`);
  };

  const handleViewMatches = () => {
    navigate(`/tournaments/${id}/matches`);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Abierto';
      case 'in_progress': return 'En Progreso';
      case 'completed': return 'Terminado';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'success';
      case 'in_progress': return 'warning';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '80vh',
        gap: 2
      }}>
        <Alert severity="error">{error || 'Torneo no encontrado o error al cargar'}</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/tournaments')}
          startIcon={<ArrowBackIcon />}
        >
          Volver a Torneos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '90%',
      maxWidth: '1000px',
      mx: 'auto',
      bgcolor: 'transparent'
    }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{ 
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton 
                onClick={handleBack}
                sx={{ 
                  width: 36,
                  height: 36,
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { 
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {tournament.name}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1
                }}>
                  <Chip 
                    label={getStatusLabel(tournament.status)}
                    size="small"
                    color={getStatusColor(tournament.status)}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Tournament Info Summary */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mt: 1
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {new Date(tournament.startDate).toLocaleDateString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GameIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {tournament.type === 'single_elimination' ? 'Eliminación Simple' :
                   tournament.type === 'double_elimination' ? 'Doble Eliminación' : 'Todos contra Todos'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {tournament.participants.length} participantes
                </Typography>
              </Box>
            </Stack>
            <AvatarGroup 
              max={5}
              sx={{ 
                '& .MuiAvatar-root': {
                  width: 28,
                  height: 28,
                  fontSize: '0.75rem'
                }
              }}
            >
              {tournament.participants.slice(0, 5).map((participant) => (
                <Tooltip key={participant.id} title={participant.name}>
                  <Avatar 
                    src={participant.avatar} 
                    alt={participant.name}
                  >
                    {participant.name[0]}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>
          </Box>
        </Paper>

        {error && !tournament && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Navigation Buttons Section */}
        <SectionTitle 
          title="Navegación"
          subtitle="Accede a las secciones del torneo"
        >
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={4} sm={2.4}>
              <NavButton 
                icon={<GroupIcon />} 
                label="Participantes" 
                onClick={handleViewParticipants}
              />
            </Grid>
            <Grid item xs={4} sm={2.4}>
              <NavButton 
                icon={<BracketIcon />} 
                label="Bracket" 
                onClick={handleViewBracket}
                disabled={!tournament.matches || tournament.matches.length === 0}
              />
            </Grid>
            <Grid item xs={4} sm={2.4}>
              <NavButton 
                icon={<CalendarIcon />} 
                label="Calendario" 
                onClick={handleViewCalendar}
              />
            </Grid>
            <Grid item xs={4} sm={2.4}>
              <NavButton 
                icon={<LeaderboardIcon />} 
                label="Posiciones" 
                onClick={handleViewLeaderboard}
              />
            </Grid>
            <Grid item xs={4} sm={2.4}>
              <NavButton 
                icon={<MatchesIcon />} 
                label="Partidas" 
                onClick={handleViewMatches}
                disabled={!tournament.matches || tournament.matches.length === 0}
              />
            </Grid>
          </Grid>
        </SectionTitle>

        {/* Tournament Info and Actions */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            {/* Tournament Information Section */}
            <SectionTitle 
              title="Información General"
            >
              <Typography variant="body1" paragraph>
                <strong>Reglas:</strong> {tournament.rules}
              </Typography>
              
              {tournament.status === 'open' && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleJoinTournament}
                  sx={{
                    mt: 2,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    p: 1.2,
                    borderColor: '#c89e38',
                    color: '#c89e38',
                    '&:hover': {
                      borderColor: '#b38d2d',
                      bgcolor: 'rgba(200, 158, 56, 0.04)'
                    }
                  }}
                >
                  Inscribirse al Torneo
                </Button>
              )}
            </SectionTitle>

            {/* Tournament Summary */}
            <SectionTitle 
              title="Resumen de Participantes"
              subtitle={`${tournament.participants.length} jugadores inscritos`}
            >
              <Grid container spacing={2}>
                {tournament.participants.slice(0, 6).map((participant) => (
                  <Grid item xs={6} sm={4} key={participant.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Avatar 
                        src={participant.avatar}
                        sx={{ width: 32, height: 32 }}
                      >
                        {participant.name[0]}
                      </Avatar>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}
                      >
                        {participant.name}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
                {tournament.participants.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      py: 3,
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <TrophyIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                      <Typography color="text.secondary" variant="body2">
                        No hay participantes registrados
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {tournament.participants.length > 6 && (
                  <Grid item xs={12}>
                    <Box textAlign="center" sx={{ mt: 1 }}>
                      <Button 
                        size="small" 
                        onClick={handleViewParticipants}
                        sx={{ 
                          color: '#c89e38',
                          '&:hover': { bgcolor: 'rgba(200, 158, 56, 0.08)' }
                        }}
                      >
                        Ver todos los participantes
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </SectionTitle>
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Announcements Section */}
            <SectionTitle 
              title="Comunicados"
              headerContent={
                user?.role === 'tournament_admin' && (
                  <Button
                    startIcon={<AnnouncementIcon />}
                    onClick={() => setOpenAnnouncementDialog(true)}
                    sx={{
                      fontSize: '0.85rem',
                      textTransform: 'none'
                    }}
                  >
                    Publicar
                  </Button>
                )
              }
            >
              <List sx={{ p: 0 }}>
                {tournament.announcements.map((announcement, index) => (
                  <React.Fragment key={announcement.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{announcement.message}</Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(announcement.date).toLocaleDateString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < tournament.announcements.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </React.Fragment>
                ))}
                {tournament.announcements.length === 0 && (
                  <Box sx={{ 
                    py: 4,
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <AnnouncementIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    <Typography color="text.secondary">
                      No hay comunicados
                    </Typography>
                  </Box>
                )}
              </List>
            </SectionTitle>
          </Grid>
        </Grid>
      </Stack>

      {/* Announcement Dialog */}
      <Dialog open={openAnnouncementDialog} onClose={() => setOpenAnnouncementDialog(false)}>
        <DialogTitle component="div">
          <Typography variant="h6">Publicar Comunicado</Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Mensaje"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={announcement}
            onChange={(e) => setAnnouncement(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnnouncementDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handlePublishAnnouncement} 
            variant="contained"
            sx={{
              bgcolor: '#c89e38',
              '&:hover': {
                bgcolor: '#b38d2d'
              }
            }}
          >
            Publicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={openAddUserDialog} onClose={() => setOpenAddUserDialog(false)}>
        <DialogTitle component="div">
          <Typography variant="h6">Agregar Jugador</Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre del Jugador"
            type="text"
            fullWidth
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddUserDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleAddParticipant} 
            variant="contained"
            sx={{
              bgcolor: '#c89e38',
              '&:hover': {
                bgcolor: '#b38d2d'
              }
            }}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TournamentDetailsPage; 